import { useMemo, useCallback } from "react";
import {
  Drawer,
  Skeleton,
  Empty,
  Typography,
  Descriptions,
  theme,
  Tag,
} from "antd";
import {
  ReactFlow,
  Controls,
  Background,
  BackgroundVariant,
  type Node,
  type Edge,
  type NodeTypes,
  MarkerType,
  Position,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import { useTranslation } from "react-i18next";
import { useWorkflow } from "../../hooks/useWorkflow";
import { useResponsiveDrawer } from "../../hooks";
import type { WorkflowActivity, WorkflowPath } from "../../types";
import { WorkflowNode } from "./WorkflowNode";
import dayjs from "dayjs";

const { Text, Title } = Typography;

interface WorkflowDrawerProps {
  itemType: string;
  itemId: string;
  open: boolean;
  onClose: () => void;
}

// Custom node types registration - use type assertion for compatibility
const nodeTypes: NodeTypes = {
  workflowNode: WorkflowNode as unknown as NodeTypes[string],
};

/**
 * Drawer component that displays a workflow visualization using xyflow.
 */
export const WorkflowDrawer: React.FC<WorkflowDrawerProps> = ({
  itemType,
  itemId,
  open,
  onClose,
}) => {
  const { t } = useTranslation();
  const { token } = theme.useToken();
  const { width: drawerWidth } = useResponsiveDrawer(800);
  const { workflow, isLoading, isError } = useWorkflow({
    itemType,
    itemId,
    enabled: open,
  });

  // Convert workflow data to xyflow nodes and edges
  const { nodes, edges } = useMemo(() => {
    if (!workflow) return { nodes: [], edges: [] };

    // Convert activities to nodes
    const flowNodes: Node[] = workflow.activities.map(
      (activity: WorkflowActivity) => ({
        id: activity.id,
        type: "workflowNode",
        position: { x: activity.x, y: activity.y },
        data: {
          label: activity.label || activity.name,
          name: activity.name,
          state: activity.state,
          nodeType: activity.nodeType,
          isStart: activity.isStart,
          isEnd: activity.isEnd,
          isAuto: activity.isAuto,
          icon: activity.icon, // Pass Aras icon to determine End node appearance
        },
        sourcePosition: Position.Right,
        targetPosition: Position.Left,
      }),
    );

    // Convert paths to edges - use smoothstep for orthogonal paths like Aras Innovator
    const flowEdges: Edge[] = workflow.paths.map((path: WorkflowPath) => ({
      id: path.id,
      source: path.sourceActivityId,
      target: path.targetActivityId,
      label: path.label || path.name || undefined,
      type: "smoothstep", // orthogonal paths like Aras Innovator
      animated: path.isDefault,
      style: {
        stroke: path.isDefault ? token.colorPrimary : token.colorBorder,
        strokeWidth: path.isDefault ? 2 : 1.5,
      },
      labelStyle: {
        fontSize: 10,
        fill: token.colorTextSecondary,
        fontWeight: 500,
      },
      labelBgStyle: {
        fill: token.colorBgContainer,
        fillOpacity: 0.85,
      },
      labelBgPadding: [4, 6] as [number, number],
      markerEnd: {
        type: MarkerType.ArrowClosed,
        color: path.isDefault ? token.colorPrimary : token.colorBorder,
        width: 16,
        height: 16,
      },
    }));

    return { nodes: flowNodes, edges: flowEdges };
  }, [workflow, token]);

  // Fit view on nodes change
  const onNodesChange = useCallback(() => {
    // Placeholder for any node change handling
  }, []);

  // Get status tag color
  const getStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case "active":
        return "processing";
      case "closed":
        return "success";
      case "cancelled":
        return "error";
      default:
        return "default";
    }
  };

  return (
    <Drawer
      title={t("workflow.title", "Workflow Process")}
      placement="right"
      width={drawerWidth}
      open={open}
      onClose={onClose}
      styles={{
        body: { padding: 0, display: "flex", flexDirection: "column" },
      }}
    >
      {isLoading ? (
        <div style={{ padding: 24 }}>
          <Skeleton active paragraph={{ rows: 6 }} />
        </div>
      ) : isError || !workflow ? (
        <div style={{ padding: 24 }}>
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={t("workflow.noWorkflow", "No workflow assigned")}
          />
        </div>
      ) : (
        <>
          {/* Workflow Info Header */}
          <div
            style={{
              padding: "16px 24px",
              borderBottom: `1px solid ${token.colorBorder}`,
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: 12,
              }}
            >
              <Title level={5} style={{ margin: 0 }}>
                {workflow.label || workflow.name}
              </Title>
              <Tag color={getStatusColor(workflow.status)}>
                {workflow.status}
              </Tag>
            </div>

            <Descriptions size="small" column={2}>
              {workflow.startedBy && (
                <Descriptions.Item
                  label={t("workflow.startedBy", "Started By")}
                >
                  {workflow.startedBy}
                </Descriptions.Item>
              )}
              {workflow.activeDate && (
                <Descriptions.Item
                  label={t("workflow.activeDate", "Active Date")}
                >
                  {dayjs(workflow.activeDate).format("YYYY-MM-DD HH:mm")}
                </Descriptions.Item>
              )}
              {workflow.processOwner && (
                <Descriptions.Item
                  label={t("workflow.processOwner", "Process Owner")}
                >
                  {workflow.processOwner}
                </Descriptions.Item>
              )}
              {workflow.closedDate && (
                <Descriptions.Item
                  label={t("workflow.closedDate", "Closed Date")}
                >
                  {dayjs(workflow.closedDate).format("YYYY-MM-DD HH:mm")}
                </Descriptions.Item>
              )}
            </Descriptions>

            {workflow.description && (
              <Text
                type="secondary"
                style={{ display: "block", marginTop: 8, fontSize: 12 }}
              >
                {workflow.description}
              </Text>
            )}
          </div>

          {/* Workflow Diagram */}
          <div style={{ flex: 1, minHeight: 400 }}>
            <ReactFlow
              nodes={nodes}
              edges={edges}
              nodeTypes={nodeTypes}
              onNodesChange={onNodesChange}
              fitView
              fitViewOptions={{ padding: 0.2 }}
              attributionPosition="bottom-left"
              proOptions={{ hideAttribution: true }}
              nodesDraggable={false}
              nodesConnectable={false}
              elementsSelectable={false}
              panOnDrag
              zoomOnScroll
              minZoom={0.3}
              maxZoom={2}
            >
              <Controls showInteractive={false} />
              <Background
                variant={BackgroundVariant.Dots}
                gap={16}
                size={1}
                color={token.colorBorderSecondary}
              />
            </ReactFlow>
          </div>

          {/* Legend - All nodes use circle shape with different colors */}
          <div
            style={{
              padding: "12px 24px",
              borderTop: `1px solid ${token.colorBorder}`,
              display: "flex",
              gap: 16,
              flexWrap: "wrap",
            }}
          >
            <LegendItem
              color="#1890ff"
              label={t("workflow.legend.start", "Start")}
            />
            <LegendItem
              color="#13c2c2"
              label={t("workflow.legend.auto", "Auto")}
              shape="diamond"
              dashed
            />
            <LegendItem
              color={token.colorWarning}
              label={t("workflow.legend.active", "Active")}
            />
            <LegendItem
              color={token.colorSuccess}
              label={t("workflow.legend.completed", "Completed")}
            />
            <LegendItem
              color={token.colorTextDisabled}
              label={t("workflow.legend.pending", "Pending")}
            />
            <LegendItem
              color="#ff4d4f"
              label={t("workflow.legend.end", "End")}
            />
          </div>
        </>
      )}
    </Drawer>
  );
};

// Legend item component - supports circle and diamond shapes
interface LegendItemProps {
  color: string;
  label: string;
  dashed?: boolean;
  shape?: "circle" | "diamond";
}

const LegendItem: React.FC<LegendItemProps> = ({
  color,
  label,
  dashed,
  shape = "circle",
}) => {
  const { token } = theme.useToken();

  return (
    <div style={{ display: "flex", alignItems: "center" }}>
      <svg
        width="14"
        height="14"
        viewBox="0 0 14 14"
        style={{ marginRight: 6 }}
      >
        {shape === "diamond" ? (
          <polygon
            points="7,1 13,7 7,13 1,7"
            fill="none"
            stroke={color}
            strokeWidth="1.5"
            strokeDasharray={dashed ? "2,1" : undefined}
          />
        ) : (
          <circle
            cx="7"
            cy="7"
            r="5.5"
            fill="none"
            stroke={color}
            strokeWidth="2"
            strokeDasharray={dashed ? "2,1" : undefined}
          />
        )}
      </svg>
      <Text style={{ fontSize: 12, color: token.colorTextSecondary }}>
        {label}
      </Text>
    </div>
  );
};

export default WorkflowDrawer;
